import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { MiniSparkline } from "@/components/Charts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { fmtCurrency, fmtPct, gainClass } from "@/lib/format";
import { PortfolioStrip } from "@/components/dashboard/PortfolioStrip";
import { Eye, Plus, Trash2, ListChecks, X, Bell } from "lucide-react";

const NEW = "__new__";

function AddSymbolDialog({ open, onOpenChange, subs, activeSub, onAdded }) {
    const [ticker, setTicker] = useState("");
    const [sub, setSub] = useState(activeSub && activeSub !== "ALL" ? activeSub : (subs[0]?.name || ""));
    const [newList, setNewList] = useState("");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!ticker.trim()) return toast.error("Enter a symbol");
        let targetSub = sub;
        setSaving(true);
        try {
            if (sub === NEW) {
                if (!newList.trim()) { setSaving(false); return toast.error("Enter a name for the new list"); }
                await api.post("/sub-watchlists", { name: newList.trim() });
                targetSub = newList.trim();
            }
            await api.post("/watchlist", { ticker: ticker.trim().toUpperCase(), sub_watchlist: targetSub });
            toast.success(`${ticker.trim().toUpperCase()} added to ${targetSub}`);
            setTicker(""); setNewList("");
            onOpenChange(false);
            onAdded?.(targetSub);
        } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-sm" data-testid="watchlist-add-dialog">
                <DialogHeader><DialogTitle className="font-heading">Add to Watchlist</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label>Symbol</Label>
                        <Input value={ticker} onChange={(e) => setTicker(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && save()}
                            placeholder="e.g. AAPL" className="num uppercase" data-testid="watchlist-ticker-input" />
                    </div>
                    <div className="space-y-1.5">
                        <Label>List</Label>
                        <Select value={sub} onValueChange={setSub}>
                            <SelectTrigger data-testid="watchlist-addsub-select"><SelectValue placeholder="Select a list" /></SelectTrigger>
                            <SelectContent>
                                {subs.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                                <SelectItem value={NEW}>+ New list…</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {sub === NEW && (
                        <div className="space-y-1.5">
                            <Label>New list name</Label>
                            <Input value={newList} onChange={(e) => setNewList(e.target.value)} placeholder="e.g. Semis to watch" data-testid="watchlist-newlist-input" />
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={save} disabled={saving} data-testid="watchlist-add-save">Add symbol</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ManageListsDialog({ open, onOpenChange, subs, onChanged }) {
    const [names, setNames] = useState({});
    const [newName, setNewName] = useState("");
    const [busy, setBusy] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const nameFor = (s) => (names[s.id] ?? s.name);

    const addList = async () => {
        if (!newName.trim()) return;
        setBusy(true);
        try { await api.post("/sub-watchlists", { name: newName.trim() }); setNewName(""); toast.success("List created"); onChanged?.(); }
        catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
    };
    const rename = async (s) => {
        const nm = nameFor(s).trim();
        if (!nm || nm === s.name) return;
        setBusy(true);
        try { await api.put(`/sub-watchlists/${s.id}`, { name: nm }); toast.success("Renamed"); onChanged?.(); }
        catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
    };
    const del = async () => {
        setBusy(true);
        try { const { data } = await api.delete(`/sub-watchlists/${deleting.id}`); toast.success(`Deleted "${deleting.name}"${data.removed_symbols ? ` and ${data.removed_symbols} symbol(s)` : ""}`); onChanged?.(); }
        catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); setDeleting(null); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-sm" data-testid="watchlist-manage-dialog">
                <DialogHeader><DialogTitle className="font-heading">Manage lists</DialogTitle></DialogHeader>
                <div className="flex gap-2 mb-3">
                    <Input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addList()} placeholder="New list name" data-testid="manage-newlist-input" />
                    <Button onClick={addList} disabled={busy} data-testid="manage-newlist-add"><Plus className="w-4 h-4" /></Button>
                </div>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                    {subs.map((s) => (
                        <div key={s.id} className="flex items-center gap-2" data-testid={`sublist-row-${s.name}`}>
                            <Input value={nameFor(s)} onChange={(e) => setNames((n) => ({ ...n, [s.id]: e.target.value }))}
                                onBlur={() => rename(s)} onKeyDown={(e) => e.key === "Enter" && rename(s)} className="h-8" data-testid={`sublist-name-${s.name}`} />
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-loss shrink-0" onClick={() => setDeleting(s)} data-testid={`sublist-delete-${s.name}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                    ))}
                    {subs.length === 0 && <p className="text-sm text-muted-foreground">No lists yet.</p>}
                </div>
                <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
                    <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete "{deleting?.name}"?</AlertDialogTitle>
                            <AlertDialogDescription>This permanently deletes the list and all symbols it contains. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-loss text-white hover:bg-loss/90" data-testid="sublist-confirm-delete">Delete</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </DialogContent>
        </Dialog>
    );
}

// How many watched stocks are up / down / flat today (no price yet counts as flat)
function upDownCells(items) {
    const up = items.filter((r) => (r.change_percent ?? 0) > 0).length;
    const down = items.filter((r) => (r.change_percent ?? 0) < 0).length;
    const flat = items.length - up - down;
    const share = (n) => `${Math.round((n / items.length) * 100)}% of ${items.length} watched`;
    return [
        { testId: "wstat-up", label: "Up Today", value: String(up), valueClass: up ? "text-gain" : "", sub: share(up) },
        { testId: "wstat-down", label: "Down Today", value: String(down), valueClass: down ? "text-loss" : "", sub: share(down) },
        { testId: "wstat-flat", label: "Unchanged / No Price", value: String(flat), sub: share(flat) },
    ];
}

export default function Watchlist() {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [selectedSub, setSelectedSub] = useState("ALL");
    const [addOpen, setAddOpen] = useState(false);
    const [manageOpen, setManageOpen] = useState(false);
    const [removing, setRemoving] = useState(null);

    const subsQ = useQuery({ queryKey: ["sub-watchlists"], queryFn: async () => (await api.get("/sub-watchlists")).data });
    const wl = useQuery({ queryKey: ["watchlist", selectedSub], queryFn: async () => (await api.get(`/watchlist?sub=${encodeURIComponent(selectedSub)}`)).data });
    const notesQ = useQuery({ queryKey: ["notes"], queryFn: async () => (await api.get("/notes")).data });
    const alertsQ = useQuery({ queryKey: ["alerts"], queryFn: async () => (await api.get("/alerts")).data });
    const noteMap = Object.fromEntries((notesQ.data || []).map((n) => [n.ticker, n.note]));
    const alertSet = new Set((alertsQ.data?.alerts || []).filter((a) => a.status !== "dismissed").map((a) => a.ticker));
    const subs = subsQ.data || [];
    const items = wl.data?.items || [];

    const refetch = () => { qc.invalidateQueries({ queryKey: ["watchlist"] }); qc.invalidateQueries({ queryKey: ["sub-watchlists"] }); };
    const remove = async () => {
        try { await api.delete(`/watchlist/${removing.id}`); toast.success(`Removed ${removing.ticker}`); refetch(); }
        catch (e) { toast.error(apiErr(e)); } finally { setRemoving(null); }
    };

    return (
        <div className="space-y-3" data-testid="watchlist-page">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-heading text-2xl font-bold tracking-tight">Watchlist</h1>
                    <p className="text-sm text-muted-foreground">Track symbols you don't own — {wl.data?.source || "Yahoo Finance (delayed)"}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Select value={selectedSub} onValueChange={setSelectedSub}>
                        <SelectTrigger className="w-[160px]" data-testid="watchlist-sub-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ALL">All lists</SelectItem>
                            {subs.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={() => setManageOpen(true)} data-testid="watchlist-manage-btn"><ListChecks className="w-4 h-4 mr-1.5" />Manage lists</Button>
                    <Button onClick={() => setAddOpen(true)} data-testid="watchlist-add-btn"><Plus className="w-4 h-4 mr-1.5" />Add symbol</Button>
                </div>
            </div>

            {items.length > 0 && <PortfolioStrip cols={3} cells={upDownCells(items)} />}

            {wl.isLoading ? <Skeleton className="h-64 rounded-xl" /> : items.length === 0 ? (
                <Card>
                    <EmptyState icon={Eye} title="Your watchlist is empty"
                        description="Add symbols you'd like to keep an eye on. They're tracked separately from your portfolio and never affect your holdings."
                        actionLabel="Add your first symbol" onAction={() => setAddOpen(true)} testId="watchlist-empty" />
                </Card>
            ) : (
                <Card className="overflow-hidden">
                    <div className="overflow-auto">
                        <table className="w-full text-[13px]" data-testid="watchlist-table">
                            <thead className="border-b border-border">
                                <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
                                    <th className="px-3 py-2 text-left sticky left-0 bg-card z-10">Symbol</th>
                                    <th className="hidden sm:table-cell px-3 py-2 text-left">Name</th>
                                    <th className="px-3 py-2 text-right">Last Price</th>
                                    <th className="px-3 py-2 text-right">Day</th>
                                    <th className="px-3 py-2 text-right hidden md:table-cell">52W Range</th>
                                    <th className="px-3 py-2 text-left hidden lg:table-cell">Sector</th>
                                    <th className="px-3 py-2 text-center hidden sm:table-cell">30D</th>
                                    <th className="px-3 py-2 text-right">List</th>
                                    <th className="px-3 py-2 text-left">Note</th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((r) => (
                                    <tr key={r.id} onClick={() => navigate(`/watchlist/${r.ticker}`)}
                                        className="border-b border-border/50 hover:bg-muted/40 cursor-pointer" data-testid={`watchlist-row-${r.ticker}`}>
                                        <td className="px-3 py-1.5 text-left font-semibold num sticky left-0 bg-card">
                                            <span className="inline-flex items-center gap-1.5">{r.ticker}{alertSet.has(r.ticker) && <Bell className="w-3 h-3 text-primary" data-testid={`watchlist-alert-${r.ticker}`} />}</span>
                                        </td>
                                        <td className="hidden sm:table-cell px-3 py-1.5 text-left text-muted-foreground truncate max-w-[200px]">{r.name}</td>
                                        <td className="px-3 py-1.5 text-right num">{r.price != null ? fmtCurrency(r.price, { decimals: 2 }) : "—"}</td>
                                        <td className={`px-3 py-1.5 text-right num font-medium ${gainClass(r.change_percent)}`}>{r.change_percent != null ? fmtPct(r.change_percent) : "—"}</td>
                                        <td className="px-3 py-1.5 text-right num hidden md:table-cell text-muted-foreground">{r.week52low != null && r.week52high != null ? `${fmtCurrency(r.week52low, { decimals: 2 })} – ${fmtCurrency(r.week52high, { decimals: 2 })}` : "—"}</td>
                                        <td className="px-3 py-1.5 text-left hidden lg:table-cell"><span className="text-xs text-muted-foreground">{r.sector}</span></td>
                                        <td className="px-3 py-1.5 hidden sm:table-cell" data-testid={`watchlist-spark-${r.ticker}`}>
                                            <div className="flex justify-center"><MiniSparkline values={r.spark || []} color={(r.change_percent ?? 0) >= 0 ? "hsl(var(--gain))" : "hsl(var(--loss))"} /></div>
                                        </td>
                                        <td className="px-3 py-1.5 text-right"><Badge variant="secondary" className="text-[10px]">{r.sub_watchlist}</Badge></td>
                                        <td className="px-3 py-1.5 text-left text-xs text-muted-foreground max-w-[160px] truncate" title={noteMap[r.ticker] || ""} data-testid={`watchlist-note-${r.ticker}`}>{noteMap[r.ticker] ? (noteMap[r.ticker].length > 18 ? noteMap[r.ticker].slice(0, 18) + "…" : noteMap[r.ticker]) : ""}</td>
                                        <td className="px-3 py-1.5 text-right">
                                            <Button variant="ghost" size="icon" className="h-7 w-7 text-loss" onClick={(e) => { e.stopPropagation(); setRemoving(r); }} data-testid={`watchlist-remove-${r.ticker}`}><X className="w-3.5 h-3.5" /></Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            <AddSymbolDialog open={addOpen} onOpenChange={setAddOpen} subs={subs} activeSub={selectedSub}
                onAdded={(target) => { refetch(); if (target && selectedSub !== "ALL") setSelectedSub(target); }} />
            <ManageListsDialog open={manageOpen} onOpenChange={setManageOpen} subs={subs} onChanged={refetch} />

            <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Remove {removing?.ticker}?</AlertDialogTitle>
                        <AlertDialogDescription>This removes {removing?.ticker} from "{removing?.sub_watchlist}". You can add it back anytime.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-loss text-white hover:bg-loss/90" data-testid="watchlist-confirm-remove">Remove</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
